import { Control, FieldErrors, useWatch } from 'react-hook-form';
import dayjs from 'dayjs';
import { DecomPolicyFormValues } from '../../decom-policy-form-layout.tsx';
import VaultDatePicker from './vault-date-picker.tsx';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

interface VaultDateRangePickerProps {
  day: number;
  idx: number;
  control: Control<DecomPolicyFormValues>;
  errors: FieldErrors<DecomPolicyFormValues>;
  label?: string; // e.g. "Date Sent (optional)" or "Date Modified (optional)"
  tooltipText?: string; // e.g. "Only emails sent within this date range will be exported."
}

export default function VaultDateRangePicker({
  day,
  idx,
  control,
  errors,
  label = 'Date Sent (optional)',
  tooltipText,
}: VaultDateRangePickerProps) {
  const vaultStartTime = useWatch({
    control,
    name: `actions.${day}.actions.${idx}.vaultStartTime`,
  });
  const vaultEndTime = useWatch({
    control,
    name: `actions.${day}.actions.${idx}.vaultEndTime`,
  });

  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          {label}
        </Typography>
        {tooltipText && (
          <Tooltip title={tooltipText} arrow>
            <InfoOutlined fontSize="small" color="action" sx={{ mb: 0.5 }} />
          </Tooltip>
        )}
      </Stack>
      <Stack direction="row" gap={2}>
        <VaultDatePicker
          day={day}
          idx={idx}
          name={`actions.${day}.actions.${idx}.vaultStartTime`}
          label="Start Date"
          control={control}
          errors={errors}
          maxDate={
            vaultEndTime && dayjs(vaultEndTime).isValid()
              ? dayjs(vaultEndTime)
              : undefined
          }
        />
        <VaultDatePicker
          day={day}
          idx={idx}
          name={`actions.${day}.actions.${idx}.vaultEndTime`}
          label="End Date"
          control={control}
          errors={errors}
          minDate={
            vaultStartTime && dayjs(vaultStartTime).isValid()
              ? dayjs(vaultStartTime)
              : undefined
          }
        />
      </Stack>
    </Box>
  );
}
